import {Plus, Search, X, ShoppingCart, DollarSign, Store} from "lucide-react";
import {useState, useRef, useEffect} from "react";
import apiFetch from "../apiClient";
import { BarcodeScanner } from "./BarcodeScanner";

const MAX_ITEMS = 15;

export function ShoppingListCard() {
  const[items, setItems] = useState([]);
  const[inputValue, setInputValue] = useState("");
  const[showDropdown, setShowDropdown] = useState(false);
  const[filteredSuggestions, setFilteredSuggestions] = useState([]);
  const[selectedIndex, setSelectedIndex] = useState(-1);
  const[searchingSuggestions, setSearchingSuggestions] = useState(false);
  const[comparandoMercados, setComparandoMercados] = useState(false);
  const[comparacaoMercados, setComparacaoMercados] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const termo = inputValue.trim();
    setComparacaoMercados(null);
    if(termo === "" || /^\d+$/.test(termo)) {
      setFilteredSuggestions([]);
      setShowDropdown(false);
      setSearchingSuggestions(false);
      return;
    }

    setSearchingSuggestions(true);
    const timeoutId = setTimeout(async () => {
      try {
        const data = await apiFetch(`/api/dashboard/nota-parana-search?termo=${encodeURIComponent(termo)}`);
        setFilteredSuggestions(data?.data ?? []);
        setShowDropdown((data?.data ?? []).length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error searching Nota Paran\u00e1 products:', error);
        setFilteredSuggestions([]);
        setShowDropdown(false);
      } finally {
        setSearchingSuggestions(false);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if(dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const searchProduct = async(itemName, itemGtin, inlineResult) => {
    if (inlineResult) {
      return inlineResult;
    }
    if (!itemGtin) {
      return null;
    }

    try {
      const data = await apiFetch(`/api/dashboard/product-prices?gtin=${itemGtin}`);
      return data;
    } catch (error) {
      console.error('Error fetching product prices:', error);
      throw error;
    }
  };

  const addItem = async (itemName, itemGtin, inlineResult) => {
    const isGtin = /^\d+$/.test(inputValue.trim());
    const nameToAdd = itemName || (isGtin ? inputValue.trim() : inputValue.trim());
    const gtinToUse = itemGtin || (isGtin ? inputValue.trim() : null);
    if(nameToAdd === "" || items.length >= MAX_ITEMS) return;

    const newItem = {
      id: Date.now(),
      name: nameToAdd,
      gtin: gtinToUse,
      searching: true,
    };

    setItems([...items, newItem]);
    setInputValue("");
    setShowDropdown(false);
    setSelectedIndex(-1);

    try {
      const result = await searchProduct(newItem.name, newItem.gtin, inlineResult);
      setItems(prevItems =>
        prevItems.map(item => {
          if (item.id !== newItem.id) return item;
          const nameIsGtin = /^\d+$/.test(item.name);
          const name = (nameIsGtin && result?.nome) ? result.nome : item.name;
          return { ...item, searching: false, result, name };
        })
      );
    } catch (error) {
      setItems(prevItems =>
        prevItems.map(item =>
          item.id === newItem.id
            ? { ...item, searching: false }
            : item
        )
      );
    }
  };

  const removeItem = (id) => {
    setItems(items.filter(item => item.id !== id));
  };

  const handleKeyPress = (e) => {
    if(e.key === "Enter") {
      e.preventDefault();
      if(selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
        const s = filteredSuggestions[selectedIndex];
        addItem(s.nome, s.gtin, s);
      } else {
        addItem();
      }
    } else if(e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if(e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev > 0 ? prev - 1 : -1
      );
    } else if(e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleSugestionClick = (sugestion) => {
    addItem(sugestion.nome, sugestion.gtin, sugestion);
  };

  const handleScanned = (scannedGtin) => {
    addItem(scannedGtin, scannedGtin);
  };

  const handleCompararMercados = async () => {
    const termo = inputValue.trim();
    if (termo === "" || /^\d+$/.test(termo)) return;

    setComparandoMercados(true);
    try {
      const data = await apiFetch(`/api/dashboard/nota-parana-comparar-mercados?termo=${encodeURIComponent(termo)}`);
      setComparacaoMercados(data?.data ?? []);
    } catch (error) {
      console.error('Error comparing Nota Paraná prices by market:', error);
      setComparacaoMercados([]);
    } finally {
      setComparandoMercados(false);
    }
  };

  const handleMercadoClick = (mercado) => {
    if (!mercado.encontrado) return;
    addItem(mercado.nome, mercado.gtin, mercado);
  };

  return (
    <div className="card custom-card h-100">
      <div className="card-body p-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-2">
            <ShoppingCart size={20} className="text-primary-custom" />
            <h3 className="h5 fw-semibold mb-0">Criar Lista de Compras</h3>
          </div>
          <span className="badge badge-secondary-custom">
            {items.length}/{MAX_ITEMS} itens
          </span>
        </div>
        
        {/* Input com auto-complete */}
        <div className="mb-4" style={{ position: "relative" }} ref={dropdownRef}>
          <div className="input-group">
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder="Buscar novo item..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onFocus={() => {
                if (filteredSuggestions.length > 0) {
                  setShowDropdown(true);
                }
              }}
              disabled={items.length >= MAX_ITEMS}
              style={{
                border: "1px solid var(--color-border)",
                color: "var(--color-text-primary)",
                borderRadius: "0.5rem 0 0 0.5rem"
              }}
            />
            <button
              className="btn btn-primary-custom"
              onClick={() => addItem()}
              disabled={items.length >= MAX_ITEMS || inputValue.trim() === ""}
              style={{ borderRadius: "0 0.5rem 0.5rem 0" }}
            >
              <Plus size={16} />
            </button>
          </div>
          {/* Auto-complete Dropdown */}
          {showDropdown && filteredSuggestions.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "0.5rem",
                marginTop: "0.25rem",
                maxHeight: "250px",
                overflowY: "auto",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)"
              }}
            >
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion.gtin || suggestion.nome}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSugestionClick(suggestion)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSugestionClick(suggestion);
                    }
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  style={{
                    padding: "0.75rem 1rem",
                    cursor: "pointer",
                    backgroundColor: selectedIndex === index ? "rgba(0, 255, 136, 0.1)" : "transparent",
                    borderBottom: index < filteredSuggestions.length - 1 ? "1px solid rgba(45, 55, 72, 0.3)" : "none",
                    color: selectedIndex === index ? "var(--color-primary)" : "var(--color-text-primary)",
                    transition: "all 0.15s ease"
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <Search size={14} className="text-muted-custom" />
                    <span>{suggestion.nome}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {searchingSuggestions && filteredSuggestions.length === 0 && (
            <div
              className="d-flex align-items-center gap-2 text-muted-custom"
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "0.5rem",
                marginTop: "0.25rem",
                padding: "0.75rem 1rem"
              }}
            >
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Buscando...</span>
              </div>
              <span className="small">Buscando na Nota Paraná...</span>
            </div>
          )}
          {items.length >= MAX_ITEMS && (
            <small className="text-danger-custom d-block mt-2">
              Máximo de {MAX_ITEMS} itens atingido
            </small>
          )}
          <div className="mt-2 d-md-none">
            <BarcodeScanner onDetected={handleScanned} />
          </div>

          {inputValue.trim() !== "" && !/^\d+$/.test(inputValue.trim()) && (
            <div className="mt-2">
              <button
                className="btn btn-outline-custom d-flex align-items-center gap-2"
                type="button"
                onClick={handleCompararMercados}
                disabled={comparandoMercados}
              >
                {comparandoMercados ? (
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Comparando...</span>
                  </div>
                ) : (
                  <Store size={16} />
                )}
                Comparar nos maiores mercados
              </button>
            </div>
          )}

          {comparacaoMercados && (
            <div
              className="mt-2"
              style={{
                border: "1px solid var(--color-border)",
                borderRadius: "0.5rem",
                overflow: "hidden"
              }}
            >
              {comparacaoMercados.length === 0 && (
                <p className="small text-muted-custom mb-0 p-3">
                  Não foi possível comparar preços agora.
                </p>
              )}
              {comparacaoMercados.map((mercado, index) => (
                <div
                  key={mercado.rede}
                  role={mercado.encontrado ? "button" : undefined}
                  tabIndex={mercado.encontrado ? 0 : undefined}
                  onClick={() => handleMercadoClick(mercado)}
                  onKeyDown={(e) => {
                    if (mercado.encontrado && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      handleMercadoClick(mercado);
                    }
                  }}
                  className="d-flex align-items-center justify-content-between px-3 py-2"
                  style={{
                    cursor: mercado.encontrado ? "pointer" : "default",
                    borderBottom: index < comparacaoMercados.length - 1 ? "1px solid var(--color-border)" : "none",
                    backgroundColor: "var(--color-surface)"
                  }}
                >
                  <span className="small fw-medium">{mercado.rede}</span>
                  {mercado.encontrado ? (
                    <span className="small fw-semibold text-primary-custom">
                      R$ {mercado.min}
                    </span>
                  ) : (
                    <span className="small text-muted-custom">Sem dados nesta busca</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Lista de Itens Adicionados */}
        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          {items.length === 0 ? (
            <div className="text-center py-5">
              <Search size={48} className="text-muted-custom mb-3" />
              <p className="text-muted-custom">
                Adicione itens à sua lista de compras
              </p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="transaction-item">
                <div className="d-flex align-items-start justify-content-between">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <p className="fw-medium mb-0">{item.name}</p>
                      {item.searching && (
                        <div className="spinner-border spinner-border-sm text-primary-custom" role="status">
                          <span className="visually-hidden">Buscando...</span>
                        </div>
                      )}
                    </div>

                    {item.result && !item.searching && (
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-primary-custom">R</span><DollarSign size={14} className="text-primary-custom" />
                          <span className="small fw-semibold text-primary-custom">
                            {item.result.min}
                          </span>
                        
                          <span className="text-muted-custom"> | </span>
                          <span className="text-attention-custom">R</span><DollarSign size={14} className="text-attention-custom" />
                          <span className="small fw-semibold text-attention-custom">
                            {item.result.media}
                          </span>
                          <span className="text-muted-custom"> | </span>
                          <span className="text-danger-custom">R</span><DollarSign size={14} className="text-danger-custom" />
                          <span className="small fw-semibold text-danger-custom">
                            {item.result.max}
                          </span>
                        </div>
                        {item.result.loja_barata && (
                          <span className="badge badge-primary-custom">
                            Loja mais barata: {item.result.loja_barata}
                          </span>
                        )}
                      </div>
                    )}
                    {!item.result && !item.searching && (
                      <p className="small text-danger-custom mb-0">
                        Nenhum produto encontrado
                      </p>
                    )}
                  </div>

                  <button
                    className="btn btn-sm p-1"
                    onClick={() => removeItem(item.id)}
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      color: "var(--color-text-secondary)"
                    }}
                    aria-label={`Remover ${item.name}`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}